import React, { useState, useEffect } from 'react';
import {
    Users, Briefcase, Award, Clock, ArrowRight,
    ChevronRight, Calendar, User, Search, MapPin
} from 'lucide-react';
import api from '../services/api';

const RecruitingOverviewTab = ({
    user,
    onNavigateToTab,
    onSelectJob
}) => {
    const [stats, setStats] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        fetchDashboardStats();
    }, []);

    const fetchDashboardStats = async () => {
        try {
            setLoading(true);
            const response = await api.get('/recruiting/dashboard-stats');
            setStats(response.data);
        } catch (error) {
            console.error('Error fetching dashboard stats:', error);
        } finally {
            setLoading(false);
        }
    };

    const getPhotoUrl = (url) => {
        if (!url) return null;
        if (url.startsWith('http')) return url;
        return `${process.env.REACT_APP_API_URL || 'http://localhost:8080'}${url}`;
    };

    // Shared pipeline stages list for progress bar calculation
    const PIPELINE_STAGES = [
        'Interview waiting',
        'Interview Scheduled',
        'Interview attended',
        'Offer Received',
        'Visa submitted',
        'Visa Granted',
        'Migrated'
    ];

    const getProgressPercentage = (stage) => {
        const defaultProgress = 10;
        if (!stage) return defaultProgress;

        const index = PIPELINE_STAGES.indexOf(stage);
        if (index === -1) return defaultProgress;

        // Scale from 0 to 100 based on the length
        const percent = Math.round((index / (PIPELINE_STAGES.length - 1)) * 100);
        return Math.max(percent, defaultProgress); // Give at least 10% if valid stage
    };

    const getEventIcon = (type) => {
        switch (type) {
            case 'assigned':
                return <Briefcase className="h-4 w-4 text-emerald-600" />;
            case 'stage_moved':
                return <ArrowRight className="h-4 w-4 text-cyan-600" />;
            case 'rejected':
                return <Users className="h-4 w-4 text-red-600" />;
            default:
                return <Award className="h-4 w-4 text-slate-600" />;
        }
    };

    const getEventBg = (type) => {
        switch (type) {
            case 'assigned': return 'bg-emerald-50 border-emerald-100/50';
            case 'stage_moved': return 'bg-cyan-50 border-cyan-100/50';
            case 'rejected': return 'bg-red-50 border-red-100/50';
            default: return 'bg-slate-50 border-slate-100/50';
        }
    };

    const getTimeAgo = (timestamp) => {
        const date = new Date(timestamp);
        const now = new Date();
        const seconds = Math.floor((now - date) / 1000);

        let interval = seconds / 31536000;
        if (interval > 1) return Math.floor(interval) + 'y ago';

        interval = seconds / 2592000;
        if (interval > 1) return Math.floor(interval) + 'mo ago';

        interval = seconds / 86400;
        if (interval >= 1) return Math.floor(interval) + 'd ago';

        interval = seconds / 3600;
        if (interval >= 1) return Math.floor(interval) + 'h ago';

        interval = seconds / 60;
        if (interval >= 1) return Math.floor(interval) + 'm ago';

        return 'Just now';
    };

    if (loading || !stats) {
        return (
            <div className="flex flex-col items-center justify-center p-20 animate-in fade-in duration-500">
                <div className="h-10 w-10 border-4 border-cyan-500/10 border-t-cyan-600 rounded-full animate-spin mb-4" />
                <p className="text-slate-500 font-medium text-sm">Loading dashboard...</p>
            </div>
        );
    }

    return (
        <div className="max-w-7xl mx-auto space-y-6 animate-in fade-in duration-700">

            {/* Welcome & Global Actions */}
            <div className="bg-white rounded-3xl p-6 sm:p-8 border border-slate-200 shadow-sm flex flex-col md:flex-row items-start md:items-center justify-between gap-6 relative overflow-hidden group">
                <div className="absolute top-0 right-0 w-96 h-96 bg-cyan-50/50 rounded-full blur-3xl pointer-events-none translate-x-1/3 -translate-y-1/3 transition-transform duration-1000 group-hover:scale-110" />

                <div className="relative z-10 flex items-center gap-5">
                    <div className="h-16 w-16 bg-gradient-to-br from-cyan-500 to-cyan-700 rounded-2xl flex items-center justify-center shadow-lg shadow-cyan-500/20 text-white font-black text-2xl border-4 border-white">
                        {user?.name?.charAt(0)?.toUpperCase() || 'P'}
                    </div>
                    <div>
                        <h1 className="text-2xl sm:text-3xl font-black text-slate-800 tracking-tight">
                            Welcome back, <span className="text-cyan-700">{user?.name?.split(' ')[0] || 'Partner'}</span>
                        </h1>
                        <p className="text-slate-500 font-medium mt-1">Here is what's happening with your pipeline today.</p>
                    </div>
                </div>

                <div className="relative z-10 flex items-center gap-3 w-full md:w-auto">
                    <button
                        onClick={() => onNavigateToTab('jobs')}
                        className="flex-1 md:flex-none px-5 py-2.5 bg-white text-slate-700 font-bold rounded-xl border border-slate-200 hover:border-cyan-300 hover:bg-cyan-50 hover:text-cyan-700 shadow-sm transition-all"
                    >
                        Manage Jobs
                    </button>
                    <button
                        onClick={() => onNavigateToTab('my-requests')}
                        className="flex-1 md:flex-none px-5 py-2.5 bg-cyan-600 text-white font-bold rounded-xl hover:bg-cyan-700 shadow-lg shadow-cyan-600/20 transition-all flex items-center justify-center gap-2"
                    >
                        New Request <ArrowRight className="h-4 w-4" />
                    </button>
                </div>
            </div>

            {/* Top Level Metric Cards */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                <div className="bg-white rounded-3xl p-6 border border-slate-200 shadow-sm hover:shadow-md transition-shadow relative overflow-hidden group">
                    <div className="h-12 w-12 bg-emerald-50 rounded-2xl flex items-center justify-center text-emerald-600 mb-4 group-hover:scale-110 transition-transform">
                        <Briefcase className="h-6 w-6 stroke-[2.5]" />
                    </div>
                    <p className="text-sm font-bold text-slate-500 mb-1">Active Roles</p>
                    <div className="flex items-end gap-3">
                        <h3 className="text-3xl font-black text-slate-800">{stats.active_roles}</h3>
                        <span className="text-xs font-bold text-emerald-600 bg-emerald-50 px-2 py-1 rounded-lg mb-1">+2 This Month</span>
                    </div>
                </div>

                <div className="bg-white rounded-3xl p-6 border border-slate-200 shadow-sm hover:shadow-md transition-shadow relative overflow-hidden group">
                    <div className="h-12 w-12 bg-cyan-50 rounded-2xl flex items-center justify-center text-cyan-600 mb-4 group-hover:scale-110 transition-transform">
                        <Users className="h-6 w-6 stroke-[2.5]" />
                    </div>
                    <p className="text-sm font-bold text-slate-500 mb-1">Total Candidates</p>
                    <div className="flex items-end gap-3">
                        <h3 className="text-3xl font-black text-slate-800">{stats.total_candidates}</h3>
                    </div>
                </div>

                <div className="bg-white rounded-3xl p-6 border border-slate-200 shadow-sm hover:shadow-md transition-shadow relative overflow-hidden group">
                    <div className="h-12 w-12 bg-violet-50 rounded-2xl flex items-center justify-center text-violet-600 mb-4 group-hover:scale-110 transition-transform">
                        <Award className="h-6 w-6 stroke-[2.5]" />
                    </div>
                    <p className="text-sm font-bold text-slate-500 mb-1">Interviews Scheduled</p>
                    <div className="flex items-end gap-3">
                        <h3 className="text-3xl font-black text-slate-800">{stats.interviews_scheduled}</h3>
                    </div>
                </div>

                <div className="bg-white rounded-3xl p-6 border border-slate-200 shadow-sm hover:shadow-md transition-shadow relative overflow-hidden group">
                    <div className="h-12 w-12 bg-orange-50 rounded-2xl flex items-center justify-center text-orange-600 mb-4 group-hover:scale-110 transition-transform">
                        <Clock className="h-6 w-6 stroke-[2.5]" />
                    </div>
                    <p className="text-sm font-bold text-slate-500 mb-1">Avg Time to Hire</p>
                    <div className="flex items-end gap-3">
                        <h3 className="text-3xl font-black text-slate-800">{stats.time_to_hire_days > 0 ? `${stats.time_to_hire_days}d` : 'N/A'}</h3>
                    </div>
                </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                {/* Main Column - Pipeline & Top Candidates */}
                <div className="lg:col-span-2 space-y-6">
                    <div className="bg-white rounded-3xl border border-slate-200 shadow-sm overflow-hidden flex flex-col h-full lg:h-[500px]">
                        <div className="p-6 border-b border-slate-100 flex items-center justify-between bg-slate-50/50 shrink-0">
                            <div>
                                <h2 className="text-lg font-bold text-slate-800 flex items-center gap-2">
                                    Top Candidates Pipeline
                                </h2>
                                <p className="text-xs font-medium text-slate-500 mt-1">Candidates advancing actively through stages</p>
                            </div>
                            <button
                                onClick={() => onNavigateToTab('jobs')}
                                className="text-xs font-bold text-cyan-600 hover:text-cyan-800 bg-white border border-slate-200 px-3 py-1.5 rounded-lg shadow-sm transition-all"
                            >
                                View All
                            </button>
                        </div>

                        <div className="p-0 flex-1 overflow-y-auto custom-scrollbar divide-y divide-slate-100">
                            {stats.top_candidates && stats.top_candidates.length > 0 ? (
                                stats.top_candidates.map((candidate) => (
                                    <div key={`${candidate.job_id}-${candidate.candidate_id}`} className="p-5 hover:bg-slate-50 transition-colors group">
                                        <div className="flex items-start sm:items-center justify-between gap-4 flex-col sm:flex-row">
                                            <div className="flex items-center gap-4">
                                                <div className="relative">
                                                    {candidate.photo_url && (
                                                        <img
                                                            src={getPhotoUrl(candidate.photo_url)}
                                                            alt={candidate.candidate_name}
                                                            className="h-12 w-12 rounded-2xl object-cover bg-slate-100 border border-slate-200 shadow-sm"
                                                            style={{ display: 'block' }}
                                                            onError={(e) => {
                                                                e.target.style.display = 'none';
                                                                const fallback = e.target.nextElementSibling;
                                                                if (fallback) fallback.style.display = 'flex';
                                                            }}
                                                            onLoad={(e) => {
                                                                const fallback = e.target.nextElementSibling;
                                                                if (fallback) fallback.style.display = 'none';
                                                            }}
                                                        />
                                                    )}
                                                    <div
                                                        className="h-12 w-12 rounded-2xl bg-gradient-to-br from-cyan-100 to-cyan-200 flex items-center justify-center border border-cyan-300 shadow-sm"
                                                        style={{ display: candidate.photo_url ? 'none' : 'flex' }}
                                                    >
                                                        <span className="text-cyan-700 font-bold text-lg">{candidate.candidate_name?.charAt(0) || 'C'}</span>
                                                    </div>
                                                    <div className="absolute -bottom-1 -right-1 h-3.5 w-3.5 bg-emerald-500 rounded-full border-2 border-white" />
                                                </div>
                                                <div>
                                                    <h4 className="font-bold text-slate-800 group-hover:text-cyan-600 transition-colors">{candidate.candidate_name}</h4>
                                                    <div className="flex items-center gap-2 text-xs font-medium mt-1">
                                                        <span className="text-cyan-600 bg-cyan-50 px-2 py-0.5 rounded-md border border-cyan-100/50">{candidate.job_title}</span>
                                                    </div>
                                                </div>
                                            </div>

                                            <div className="flex items-center gap-2 w-full sm:w-auto">
                                                <button
                                                    onClick={() => {
                                                        if (candidate.job_id && onSelectJob) {
                                                            onSelectJob(candidate.job_id);
                                                        } else {
                                                            onNavigateToTab('jobs');
                                                        }
                                                    }}
                                                    className="flex-1 sm:flex-none px-4 py-2 text-xs font-bold text-slate-600 bg-white border border-slate-200 rounded-xl hover:bg-slate-50 hover:text-slate-900 shadow-sm transition-all"
                                                >
                                                    Review
                                                </button>
                                            </div>
                                        </div>

                                        <div className="mt-5 relative">
                                            <div className="flex items-center justify-between mb-2">
                                                <span className="text-xs font-bold text-slate-700">{candidate.progress_stage}</span>
                                                <span className="text-xs font-bold text-cyan-600">{getProgressPercentage(candidate.progress_stage)}% Complete</span>
                                            </div>
                                            <div className="h-2 w-full bg-slate-100 rounded-full overflow-hidden">
                                                <div
                                                    className="h-full bg-gradient-to-r from-cyan-400 to-cyan-600 rounded-full"
                                                    style={{ width: `${getProgressPercentage(candidate.progress_stage)}%` }}
                                                />
                                            </div>
                                        </div>
                                    </div>
                                ))
                            ) : (
                                <div className="p-12 flex flex-col items-center justify-center text-center">
                                    <div className="h-16 w-16 bg-slate-50 rounded-full flex items-center justify-center mb-4">
                                        <Users className="h-8 w-8 text-slate-300" />
                                    </div>
                                    <h3 className="text-sm font-bold text-slate-700 mb-1">No Active Candidates</h3>
                                    <p className="text-xs text-slate-500 max-w-xs">Candidates advancing through stages will appear here.</p>
                                </div>
                            )}
                        </div>
                    </div>
                </div>

                {/* Side Column - Activity Feed */}
                <div className="lg:col-span-1 space-y-6">
                    <div className="bg-white rounded-3xl border border-slate-200 shadow-sm overflow-hidden h-full flex flex-col lg:h-[500px]">
                        <div className="p-6 border-b border-slate-100 bg-slate-50/50 shrink-0">
                            <h2 className="text-lg font-bold text-slate-800">Recent Updates</h2>
                            <p className="text-xs font-medium text-slate-500 mt-1">Latest activity across your jobs</p>
                        </div>

                        <div className="p-6 flex-1 overflow-y-auto custom-scrollbar">
                            {stats.recent_updates && stats.recent_updates.length > 0 ? (
                                <div className="space-y-6 relative before:absolute before:inset-0 before:ml-[1.15rem] before:-translate-x-px md:before:mx-auto md:before:translate-x-0 before:h-full before:w-0.5 before:bg-gradient-to-b before:from-transparent before:via-slate-200 before:to-transparent">
                                    {stats.recent_updates.map((update, index) => (
                                        <div key={update.id || index} className="relative flex items-center justify-between md:justify-normal md:odd:flex-row-reverse group is-active">
                                            <div className="flex items-center justify-center w-10 h-10 rounded-full border-4 border-white bg-white shadow-sm shrink-0 md:order-1 md:group-odd:-translate-x-1/2 md:group-even:translate-x-1/2 z-10">
                                                <div className={`h-8 w-8 rounded-full border flex items-center justify-center ${getEventBg(update.type)}`}>
                                                    {getEventIcon(update.type)}
                                                </div>
                                            </div>

                                            <div className="w-[calc(100%-3rem)] md:w-[calc(50%-2.5rem)] bg-white p-4 rounded-2xl border border-slate-100 shadow-sm ml-4 md:ml-0 hover:border-cyan-200 hover:shadow-md transition-all">
                                                <div className="flex items-center justify-between mb-1">
                                                    <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">{getTimeAgo(update.timestamp)}</span>
                                                </div>
                                                <p className="text-sm font-bold text-slate-800 mb-1 leading-tight">
                                                    {update.candidate_name} <span className="text-slate-500 font-medium">{update.details.toLowerCase()}</span>
                                                </p>
                                                <p className="text-[11px] font-bold text-cyan-600 bg-cyan-50 px-2 py-1 rounded inline-block">
                                                    {update.job_title}
                                                </p>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            ) : (
                                <div className="h-full flex flex-col items-center justify-center text-center py-10">
                                    <Clock className="h-10 w-10 text-slate-200 mb-3" />
                                    <p className="text-xs font-bold text-slate-400">No recent activity</p>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default RecruitingOverviewTab;
